/**
 * Requests made for the account that was just signed out (or replaced by a
 * sign-in in another tab) are refused with permission-denied once Firebase
 * sends the new account's credentials. Nobody did anything wrong, so those
 * refusals are neither shown nor reported.
 */
let lastAccountChange = 0;
const WINDOW_MS = 10_000;

/** Called when the signed-in account changes (not on the first restore). */
export function noteAccountChange(): void {
  lastAccountChange = Date.now();
}

function errorText(error: unknown, depth = 0): string {
  if (!error || depth > 4) return '';
  if (typeof error === 'string') return error;
  const e = error as { code?: unknown; message?: unknown; cause?: unknown; devMessage?: unknown };
  return [e.code, e.message, e.devMessage].filter((v) => typeof v === 'string').join(' ') + ' ' + errorText(e.cause, depth + 1);
}

export function isPermissionDenied(error: unknown): boolean {
  return /permission-denied|insufficient permissions/i.test(errorText(error));
}

// The account this tab shows, and how to read the one Firebase is signed in
// as. In Safari a sign-in in another tab could leave this tab showing the
// old account for minutes: everything it asked for was refused (Sentry
// JAVASCRIPT-REACT-16, -6F, -6G), well past the 10 s window.
let shownUid: string | null = null;
let signedInUid: () => string | null = () => shownUid;
let resyncAsked = 0;

/** Called with the account the app now shows (null: signed out). */
export function noteShownAccount(uid: string | null, readSignedInUid?: () => string | null): void {
  shownUid = uid;
  if (readSignedInUid) signedInUid = readSignedInUid;
}

/** The tab shows one account while Firebase is signed in as another. */
export function showingAnotherAccount(): boolean {
  return shownUid !== null && signedInUid() !== shownUid;
}

/**
 * A permission refusal within moments of an account change, or while the tab
 * still shows an account that is no longer signed in. In the second case the
 * app is asked to load the signed-in account (once every 10 s at most).
 */
export function isStaleAccountError(error: unknown): boolean {
  if (!isPermissionDenied(error)) return false;
  if (Date.now() - lastAccountChange < WINDOW_MS) return true;
  if (!showingAnotherAccount()) return false;
  if (typeof document !== 'undefined' && Date.now() - resyncAsked > WINDOW_MS) {
    resyncAsked = Date.now();
    document.dispatchEvent(new Event('auth-refresh-required'));
  }
  return true;
}
